/**
 * AI.GEN lines=1-100
 * 调度工作台搜索参数构建工具函数
 */
import dayjs from 'dayjs';

import {
  OrderBusinessType,
  OrderTypeEnum,
  PRECISE_SEARCH_TYPE,
  SearchTypeEnum,
  TimeTypeEnum,
} from '@/components/enum';
import type { HPOrderSubOption } from '@/stores/dispatch-workbench';

/**
 * 搜索状态类型（用于参数构建函数）
 */
export interface SearchState {
  cityName?: string;
  cityLocation?: {
    latitude: string;
    longitude: string;
  };
  orderAreaIdList?: number[];
  userAreaIdList?: number[];
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
  distanceInfoList?: string[];
  preciseSearch?: string;
  preciseSearchType: string;
  orderBusinessType: OrderBusinessType[];
  checkedList: HPOrderSubOption[];
  deliveryTagList: string[];
  siteId?: number;
  timeType: 0 | 1;
}

/**
 * AI.GEN lines=1-15
 * 获取通用搜索参数
 */
export const getCommonSearchParams = (
  state: SearchState,
  options?: {
    orderBusinessType?: OrderBusinessType[];
  },
) => {
  const hasDistanceRange =
    Array.isArray(state.distanceInfoList) && state.distanceInfoList.length > 0;
  // 判断是否包含HP业务
  const hasHPOrder =
    options?.orderBusinessType?.includes(OrderBusinessType.HP) ?? true;

  return {
    cityName: state.cityName,
    cityLocation: state.cityLocation,
    // 商圈ID列表（只在HP业务时传递）
    orderAreaIdList: hasHPOrder ? state.orderAreaIdList : undefined,
    // 用户区域id集合
    userAreaIdList: state.userAreaIdList,
    startDate: state.dateRange?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
    endDate: state.dateRange?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
    distanceInfoList: hasDistanceRange ? state.distanceInfoList : undefined,
  };
};

/**
 * AI.GEN lines=1-10
 * 获取精确搜索参数
 */
export const getPreciseSearchParams = (state: SearchState) => {
  const { preciseSearchType, preciseSearch } = state;
  return {
    orderNumber:
      preciseSearchType === PRECISE_SEARCH_TYPE.ORDER_NUMBER
        ? preciseSearch
        : undefined,
    storeAddress:
      preciseSearchType === PRECISE_SEARCH_TYPE.STORE_ADDRESS
        ? preciseSearch
        : undefined,
    userAddress:
      preciseSearchType === PRECISE_SEARCH_TYPE.USER_ADDRESS
        ? preciseSearch
        : undefined,
    preciseSearch:
      preciseSearchType === PRECISE_SEARCH_TYPE.ORDER_REMARK
        ? preciseSearch
        : undefined,
  };
};

/**
 * AI.GEN lines=1-10
 * 转换精确搜索类型为搜索类型
 */
export const convertPreciseSearchTypeToSearchType = (
  preciseSearchType: string,
): SearchTypeEnum | undefined => {
  const typeMap: Record<string, SearchTypeEnum> = {
    [PRECISE_SEARCH_TYPE.ORDER_NUMBER]: SearchTypeEnum.OrderNumber,
    [PRECISE_SEARCH_TYPE.STORE_ID]: SearchTypeEnum.StoreId,
    [PRECISE_SEARCH_TYPE.STORE_ADDRESS]: SearchTypeEnum.StoreAddress,
    [PRECISE_SEARCH_TYPE.USER_ADDRESS]: SearchTypeEnum.UserAddress,
    [PRECISE_SEARCH_TYPE.ORDER_REMARK]: SearchTypeEnum.OrderRemark,
  };
  return typeMap[preciseSearchType];
};

/**
 * AI.GEN lines=1-10
 * 获取订单类型列表（从 checkedList 转换）
 */
export const getOrderTypeList = (
  checkedList: HPOrderSubOption[],
): OrderTypeEnum[] => {
  const orderTypeList: OrderTypeEnum[] = [];
  checkedList.forEach((option) => {
    switch (option) {
      case 'onlyPandaDeliveryOrders':
        orderTypeList.push(OrderTypeEnum.PandaDelivery);
        break;
      case 'farawayOrderOnly':
        orderTypeList.push(OrderTypeEnum.PandaFaraway);
        break;
      case 'showSpecialDriverOrders':
        orderTypeList.push(OrderTypeEnum.PandaSpecialDriver);
        break;
      case 'groupMeal':
        orderTypeList.push(OrderTypeEnum.GroupOrder);
        break;
    }
  });
  return orderTypeList;
};

/**
 * AI.GEN lines=1-20
 * 获取全局列表参数（包含所有列表请求的全局筛选参数）
 */
export const getGlobalListParams = (state: SearchState) => {
  const hasPFOrder = state.orderBusinessType.includes(OrderBusinessType.PF);
  const orderTypeList = getOrderTypeList(state.checkedList);

  return {
    orderBusinessTypeList: state.orderBusinessType,
    // HP 等非 PF 业务使用 orderTypeList；PF 业务使用 pfTagList
    orderTypeList:
      !hasPFOrder && orderTypeList.length > 0 ? orderTypeList : undefined,
    pfTagList:
      hasPFOrder && state.deliveryTagList.length > 0
        ? state.deliveryTagList
        : undefined,
    siteId: hasPFOrder && state.siteId ? state.siteId : undefined,
    searchType:
      state.preciseSearch && state.preciseSearch.trim()
        ? convertPreciseSearchTypeToSearchType(state.preciseSearchType)
        : undefined,
    searchContent: state.preciseSearch,
    timeType: state.timeType as TimeTypeEnum,
  };
};
